import { Template } from '../../reducers/template_reducer';
import { SnackbarProps, QueueSnackbar } from '../../actions/snackbar_actions';
import { NavigationProps, navRoutes } from '../../components/nav/Routes';
import { Lookups } from '../../utilities/lookup';
import { Strings } from '../../assets/common/strings';
import { navigateBack, navigateTo, navigateToWithoutAddingToHistory } from '../../actions/navigation_actions';
import { GetTemplateById, DeleteTemplate, PostTemplate, StoreEditedTemplate } from '../../actions/template_actions';
import { connect } from '@optum-uhone-hmkts/rise';
import React, { ChangeEvent } from 'react';
import { jwt_auth } from '../../utilities/auth';
import { themePalette } from '../../utilities/branding';
import {
	Button,
	Grid,
	Dialog,
	DialogActions,
	DialogTitle,
	DialogContent,
	DialogContentText,
	TextField,
	Switch,
	Typography,
	Card,
	CardContent,
	FormControlLabel,
	MenuItemProps,
	MenuItem,
	MenuList,
} from '@material-ui/core';
import { BasePageContainer } from '../../components/Layout/BasePage';
import { createRequiredLabel } from '../../components/utility/required_label';
import { P } from '../../utilities/auth/permissions';
import { deleteTemplateText } from '../../assets/common/string_builders';
import { EmailAutomationEvent } from '../../reducers/email_automation_reducer';
import { getEmailAutomationsByAgentId } from '../../actions/email_automation_actions';
import { makeTrimToMaxLength } from '../../utilities/formatting/string_normalization';
import { MultiselectDropdownWithChips } from '../../components/Layout/filter';
import { getSnackbarErrorProps } from '../../utilities/snackbar_util';
import { SaveCancelHeaderBarComponent } from '../../components/Layout/SaveCancelHeaderBar';

import Quill from 'quill';
import TableUp, {
	defaultCustomSelect,
	TableAlign,
	TableMenuContextmenu,
	TableResizeScale,
	TableSelection,
	TableVirtualScrollbar,
} from 'quill-table-up';
import 'quill/dist/quill.snow.css';
import 'quill-table-up/index.css';
import 'quill-table-up/table-creator.css';

let IS_TABLE_UP_REGISTERED = false;

const STI = Strings.TemplateInput;

interface StateProps {
	agentCode: string;
	agentId: string;
	isTemplateLoading: boolean;
	languages: string[];
	linesOfBusiness: string[];
	substitutions: string[];
	template: Template;
	events: EmailAutomationEvent[];
	emailAutomationEventTypeCategories: string[];
}

interface DispatchProps {
	deleteTemplate: (templateId: string) => void;
	getSnackbar: (props: SnackbarProps) => void;
	getTemplate: (templateId: string) => void;
	navigateBack: () => void;
	navigateTo: (route: string) => void;
	navigateToWithoutAddingToHistory: (route: string) => void;
	postTemplate: (template: Template) => void;
	storeEditedTemplate: (template: Template) => void;
	getEmailAutomations: () => void;
}

type Props =
	& StateProps
	& DispatchProps
	& NavigationProps;

interface State {
	templateValues: Template;

	isCancelDialogOpen: boolean;
	isDeleteDialogOpen: boolean;
	isInUseDialogOpen: boolean;
	isLinkDialogOpen: boolean;
	isVariableDialogOpen: boolean;
	linkDisplayText: string;
	linkUrl: string;

	isEditMode: boolean;
	isNewTemplate: boolean;
}

const STM = Strings.TemplateManagement;

let IS_INITIALIZING = true;

class TemplateContentPage extends React.Component<Props, State> {
	isEmailAdmin: boolean;
	isPristine: boolean;
	moreMenuChildren: MenuItemProps[] = [];
	quill: any;
	editorHostRef: React.RefObject<HTMLDivElement>;
	isApplyingQuillValue: boolean;
	pendingLinkRange: { index: number; length: number } | null;

	constructor(props: Props) {
		super(props);

		this.state = {
			templateValues: props.template,
			isCancelDialogOpen: false,
			isDeleteDialogOpen: false,
			isInUseDialogOpen: false,
			isLinkDialogOpen: false,
			isVariableDialogOpen: false,
			linkDisplayText: '',
			linkUrl: '',
			isEditMode: this.props.match.params.isEditMode === Strings.Boolean.True,
			isNewTemplate: (this.props.match.params.templateId || '') === STM.NewTemplate,
		};

		this.isEmailAdmin = jwt_auth.hasPermission(P.Admin);
		this.isPristine = true;
		this.quill = null;
		this.editorHostRef = React.createRef<HTMLDivElement>();
		this.isApplyingQuillValue = false;
		this.pendingLinkRange = null;

		IS_INITIALIZING = true;
	}

	componentDidMount() {
		this.props.getEmailAutomations();

		if (!this.props.match.params.templateId || this.props.match.params.templateId === '') {
			return;

		} else if ((this.props.match.params.templateId || STM.NewTemplate) !== STM.NewTemplate) {
			this.props.getTemplate(this.props.match.params.templateId);

		} else {
			IS_INITIALIZING = false;
			this.initializeQuillEditor();
			this.moreMenuChildren = [{
				children: Strings.MoreMenu.Edit,
				onClick: this.onClickEdit,
				disabled: false,
			}, {
				children: Strings.MoreMenu.Delete,
				onClick: this.onClickDelete,
				disabled: false,
			}];
		}
	}

	componentDidUpdate(_prevProps: Props, prevState: State) {
		if (!this.quill) {
			this.initializeQuillEditor();
		}

		if (this.quill && prevState.isEditMode !== this.state.isEditMode) {
			this.quill.enable(this.state.isEditMode);
			this.setToolbarVisibility(this.state.isEditMode);
			this.configureCustomToolbarButtons();
			this.applyToolbarTooltips();
		}

		if (
			this.quill
			&& !this.isApplyingQuillValue
			&& (this.state.templateValues.htmlContent || '') !== this.quill.root.innerHTML
		) {
			this.isApplyingQuillValue = true;
			this.quill.setText('', 'silent');
			this.quill.clipboard.dangerouslyPasteHTML(0, this.state.templateValues.htmlContent || '', 'silent');
			this.isApplyingQuillValue = false;
		}

		if (this.props.isTemplateLoading
			|| !IS_INITIALIZING
		) {
			return;
		}

		if (!this.isEmailAdmin
			&& this.state.isEditMode
			&& !this.state.isNewTemplate
			&& this.props.template
			&& this.props.template.emailType
			&& this.props.template.emailType === STM.Corporate
		) {
			this.props.getSnackbar(getSnackbarErrorProps(STM.AdminOnly));
			this.props.navigateTo(navRoutes.templateManagement.path);
			return;
		}

		if (IS_INITIALIZING) {
			const menuItemDisabled = !this.isEmailAdmin
				&& this.props.template.emailType === Strings.TemplateManagement.Corporate;

			IS_INITIALIZING = false;

			this.setState({ templateValues: this.props.template }, () => {
				this.initializeQuillEditor();
				this.moreMenuChildren = [{
					children: Strings.MoreMenu.Edit,
					onClick: this.onClickEdit,
					disabled: menuItemDisabled,
				}, {
					children: Strings.MoreMenu.Delete,
					onClick: this.onClickDelete,
					disabled: menuItemDisabled,
				}];
			});
		}
	}

	componentWillUnmount() {
		this.quill = null;
	}

	initializeQuillEditor = () => {
		if (this.quill || !this.editorHostRef.current || IS_INITIALIZING) {
			return;
		}

		if (!IS_TABLE_UP_REGISTERED) {
			Quill.register({ [`modules/${TableUp.moduleName}`]: TableUp }, true);
			IS_TABLE_UP_REGISTERED = true;
		}

		this.quill = new Quill(this.editorHostRef.current, {
			theme: 'snow',
			readOnly: !this.state.isEditMode,
			modules: {
				toolbar: {
					container: [
						['undo', 'redo'],
						['bold', 'italic', 'underline'],
						[{ color: [] }, { background: [] }],
						[{ align: [] }],
						[{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
						[{ [TableUp.toolName]: [] }],
						['link', 'clean', 'addVariables'],
					],
					handlers: {
						undo: () => this.quill?.history?.undo(),
						redo: () => this.quill?.history?.redo(),
						link: () => this.openLinkDialog(),
						addVariables: () => this.openVariableDialog(),
					},
				},
				table: false,
				[TableUp.moduleName]: {
					customSelect: defaultCustomSelect,
					modules: [
						{ module: TableVirtualScrollbar },
						{ module: TableAlign },
						{ module: TableResizeScale },
						{ module: TableSelection },
						{ module: TableMenuContextmenu },
					],
				},
			},
		});

		this.configureCustomToolbarButtons();
		this.applyToolbarTooltips();
		setTimeout(() => {
			this.configureCustomToolbarButtons();
			this.applyToolbarTooltips();
		}, 0);
		this.setToolbarVisibility(this.state.isEditMode);
		this.quill.root.style.fontFamily = 'sans-serif';
		this.quill.clipboard.dangerouslyPasteHTML(0, this.state.templateValues.htmlContent || '', 'silent');
		this.quill.on('text-change', () => {
			this.isApplyingQuillValue = true;
			this.handleEditorChange(this.quill.root.innerHTML || '');
			this.isApplyingQuillValue = false;
		});
	};

	getToolbarElement = (): HTMLElement | null => {
		if (this.quill) {
			const toolbarModule = this.quill.getModule('toolbar');
			if (toolbarModule && toolbarModule.container) {
				return toolbarModule.container as HTMLElement;
			}
		}

		const host = this.editorHostRef.current;
		if (!host) {
			return null;
		}

		const toolbarInHost = host.querySelector('.ql-toolbar') as HTMLElement | null;
		if (toolbarInHost) {
			return toolbarInHost;
		}

		const parent = host.parentElement;
		if (!parent) {
			return null;
		}

		return parent.querySelector('.ql-toolbar') as HTMLElement | null;
	};

	configureCustomToolbarButtons = () => {
		const toolbar = this.getToolbarElement();
		if (!toolbar) {
			return;
		}

		const undoButton = toolbar.querySelector('button.ql-undo') as HTMLButtonElement | null;
		if (undoButton) {
			undoButton.innerHTML = '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="7 4 3 8 7 12"></polyline><path class="ql-stroke" d="M3,8h7a4,4 0 1,1 0,8H8"></path></svg>';
			undoButton.style.width = '28px';
			undoButton.style.paddingLeft = '0';
			undoButton.style.paddingRight = '0';
		}

		const redoButton = toolbar.querySelector('button.ql-redo') as HTMLButtonElement | null;
		if (redoButton) {
			redoButton.innerHTML = '<svg viewBox="0 0 18 18"><polyline class="ql-stroke" points="11 4 15 8 11 12"></polyline><path class="ql-stroke" d="M15,8H8a4,4 0 1,0 0,8h2"></path></svg>';
			redoButton.style.width = '28px';
			redoButton.style.paddingLeft = '0';
			redoButton.style.paddingRight = '0';
		}

		const addVariablesButton = toolbar.querySelector('button.ql-addVariables') as HTMLButtonElement | null;
		if (addVariablesButton) {
			addVariablesButton.textContent = Strings.ContentEditor.Button;
			addVariablesButton.style.width = 'auto';
			addVariablesButton.style.paddingLeft = '8px';
			addVariablesButton.style.paddingRight = '8px';
		}
	};

	applyToolbarTooltips = () => {
		const toolbar = this.getToolbarElement();
		if (!toolbar) {
			return;
		}

		const setTooltip = (selector: string, title: string) => {
			const elements = toolbar.querySelectorAll(selector);
			elements.forEach(element => {
				element.setAttribute('title', title);
				element.setAttribute('aria-label', title);
			});
		};

		setTooltip('button.ql-bold', 'Bold');
		setTooltip('button.ql-italic', 'Italic');
		setTooltip('button.ql-underline', 'Underline');
		setTooltip('button.ql-list[value="ordered"]', 'Ordered list');
		setTooltip('button.ql-list[value="bullet"]', 'Bulleted list');
		setTooltip('button.ql-indent[value="-1"]', 'Decrease indent');
		setTooltip('button.ql-indent[value="+1"]', 'Increase indent');
		setTooltip('button.ql-undo', 'Undo');
		setTooltip('button.ql-redo', 'Redo');
		setTooltip('button.ql-link', 'Insert link');
		setTooltip('button.ql-addVariables', 'Add Variables');
		setTooltip('button.ql-clean', 'Clear formatting');
		setTooltip('.ql-picker.ql-table-up .ql-picker-label', 'Insert table');
		setTooltip('.ql-picker.ql-table-up .ql-picker-item', 'Table size');

		setTooltip('.ql-picker.ql-color .ql-picker-label', 'Text color');
		setTooltip('.ql-picker.ql-background .ql-picker-label', 'Background color');
		setTooltip('.ql-picker.ql-align .ql-picker-label', 'Text alignment');
		setTooltip('.ql-picker.ql-align .ql-picker-item:not([data-value])', 'Align left');
		setTooltip('.ql-picker.ql-align .ql-picker-item[data-value=""]', 'Align left');
		setTooltip('.ql-picker.ql-align .ql-picker-item[data-value="center"]', 'Align center');
		setTooltip('.ql-picker.ql-align .ql-picker-item[data-value="right"]', 'Align right');
		setTooltip('.ql-picker.ql-align .ql-picker-item[data-value="justify"]', 'Justify');
	};

	setToolbarVisibility = (isVisible: boolean) => {
		const toolbar = this.getToolbarElement();
		if (toolbar) {
			toolbar.style.display = isVisible ? '' : 'none';
		}
	};

	closeDialog = () => {
		this.pendingLinkRange = null;
		this.setState({
			isCancelDialogOpen: false,
			isDeleteDialogOpen: false,
			isInUseDialogOpen: false,
			isLinkDialogOpen: false,
			isVariableDialogOpen: false,
			linkDisplayText: '',
			linkUrl: '',
		})
	};

	openInUseDialog = () => this.setState({ isInUseDialogOpen: true });
	openDeleteDialog = () => this.setState({ isDeleteDialogOpen: true });
	openCancelDialog = () => this.setState({ isCancelDialogOpen: true });
	openVariableDialog = () => this.setState({ isVariableDialogOpen: true });

	openLinkDialog = () => {
		if (!this.quill || !this.state.isEditMode) {
			return;
		}

		const range = this.quill.getSelection(true);
		if (!range) {
			return;
		}

		this.pendingLinkRange = { index: range.index, length: range.length };

		const selectedText = range.length > 0
			? this.quill.getText(range.index, range.length)
			: '';

		const formatLength = range.length > 0 ? range.length : 1;
		const currentFormat = this.quill.getFormat(range.index, formatLength);
		const currentLink = typeof currentFormat.link === 'string'
			? currentFormat.link
			: '';

		this.setState({
			isLinkDialogOpen: true,
			linkDisplayText: selectedText,
			linkUrl: currentLink,
		});
	};

	handleLinkFieldChange = (field: 'linkDisplayText' | 'linkUrl') => (event: ChangeEvent<HTMLInputElement>) => {
		this.setState({ [field]: event.currentTarget.value } as Pick<State, 'linkDisplayText' | 'linkUrl'>);
	};

	applyLink = () => {
		const range = this.pendingLinkRange;
		if (!this.quill || !range) {
			this.closeDialog();
			return;
		}

		const linkUrl = this.state.linkUrl.trim();
		if (!linkUrl) {
			return;
		}

		const displayText = this.state.linkDisplayText.trim()
			|| this.quill.getText(range.index, range.length).trim()
			|| linkUrl;

		if (range.length > 0) {
			this.quill.deleteText(range.index, range.length, 'user');
		}

		this.quill.insertText(range.index, displayText, { link: linkUrl }, 'user');
		this.quill.setSelection(range.index + displayText.length, 0, 'silent');
		this.closeDialog();
	};

	startEditMode = () => {
		this.pendingLinkRange = null;
		this.setState({
			isEditMode: true,
			isCancelDialogOpen: false,
			isDeleteDialogOpen: false,
			isInUseDialogOpen: false,
			isLinkDialogOpen: false,
			isVariableDialogOpen: false,
			linkDisplayText: '',
			linkUrl: '',
		});
	};

	onClickDelete = () => {
		if (this.props.template
			&& this.props.template.isInUseOnAutomation
			&& !this.isEmailAdmin) {
			this.openInUseDialog();
		} else {
			this.openDeleteDialog();
		}
	};

	onDeleteConfirmation = () => {
		if (!this.state.isNewTemplate) {
			this.props.deleteTemplate(this.props.template.id);
		}

		this.props.navigateBack();
	};

	onClickEdit = () => {
		if (!this.state.isNewTemplate
			&& this.props.template.isInUseOnAutomation
			&& !this.isEmailAdmin) {
			this.openInUseDialog();
		} else {
			this.startEditMode();
		}
	};

	onClickPreview = () => {
		if (this.isValid()) {
			this.props.storeEditedTemplate(this.state.templateValues);
			this.props.navigateTo(
				navRoutes.templatePreview.path
					.replace(Strings.Navigation.TemplateId, this.props.template.id || STM.NewTemplate)
					.replace(Strings.Navigation.From, Strings.TemplateManagement.Content)
			);
		} else {
			this.props.getSnackbar(getSnackbarErrorProps(Strings.TemplateManagement.IncompleteFields));
		}
	};

	onClickSave = () => {
		if (this.isValid()) {
			this.props.postTemplate(this.state.templateValues);
			this.props.navigateToWithoutAddingToHistory(
				navRoutes.templateManagement.path
			);
		} else {
			this.props.getSnackbar(getSnackbarErrorProps(STM.FailedSave));
		}
	};

	handleEditorChange = (content: string) => {
		const templateValues: Template = {
			...this.state.templateValues,
			htmlContent: content,
		};

		this.setState({ templateValues });
	};

	handleSelectEvent = () => this.setState({
		templateValues: {
			...this.state.templateValues,
		},
	});

	handleChange = (field: string, normalize?: number, validate?: boolean) => (event: ChangeEvent<HTMLInputElement>) => {
		let text = event.currentTarget.textContent || event.currentTarget.value || '';

		if (normalize) {
			text = makeTrimToMaxLength(normalize)(text);
		}

		if (validate) {
			text = text.trimLeft();
		}

		const templateValues = {
			...this.state.templateValues,
			[field]: text,
		};

		this.setState({ templateValues });
	};

	handleLineOfBusinessChange = (newLobs: string[]) => {
		const lobs = newLobs.length === 0
			? ['All']
			: newLobs;

		this.setState(prevState => ({
			templateValues: {
				...prevState.templateValues,
				linesOfBusiness: lobs,
			},
		}));
	}
	handleEventTypeChange = (newEventTypes: string[]) => {
		const eventTypes = newEventTypes.length === 0
			? ['All']
			: newEventTypes;

		this.setState(prevState => ({
			templateValues: {
				...prevState.templateValues,
				emailAutomationEventTypeCategory: eventTypes,
			},
		}));
	}
	handleSwitchChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
		const templateValues: Template = {
			...this.state.templateValues,
			isDraft: checked,
		};

		this.setState({ templateValues });
	};

	isValid = () => {
		return this.state.templateValues.emailType
			&& this.state.templateValues.title
			&& this.state.templateValues.subject
			&& this.state.templateValues.language
	};

	navigateBack = () => {
		this.props.navigateBack();
	};

	render() {
		return (
			<BasePageContainer
				topStyle={{ textAlign: Strings.CSS.Center }}
				topComponent={this.renderTopComponent()}
				middleComponent={
					IS_INITIALIZING
						? <div />
						: this.renderMiddleComponent()
				}
			/>
		);
	}

	renderTopComponent = (): JSX.Element => {
		return (
			<SaveCancelHeaderBarComponent
				title={
					this.state.isEditMode
						? Strings.TemplateManagement.EditorTitle
						: Strings.TemplateManagement.DetailsTitle
				}
				cancelText={
					this.state.isEditMode
						? Strings.ButtonText.Cancel
						: Strings.ButtonText.Back
				}
				onCancel={
					this.state.isEditMode
						? this.openCancelDialog
						: this.navigateBack
				}
				bypassCancelConfirmation={true}
				onSave={this.onClickSave}
				secondaryButtonText={Strings.ButtonText.Preview}
				onSecondaryButtonClick={this.onClickPreview}
				isSecondaryButtonDisabled={!this.state.isEditMode}
				isSaveDisabled={this.props.template.emailType === STM.Corporate && !this.isEmailAdmin}
				isLoading={IS_INITIALIZING}
			/>
		);
	};

	renderMiddleComponent = (): JSX.Element => {
		return !IS_INITIALIZING
			? (
				<div>
					{this.renderContentEditor()}
					{this.renderCancelDialog()}
					{this.renderIsInUseDialog()}
					{this.renderDeleteDialog()}
				</div>
			) : (
				<div />
			);
	};

	renderContentEditor = (): JSX.Element => {
		const emailTypes: string[] = this.isEmailAdmin || !this.state.isEditMode
			? [STM.Corporate, STM.Agent]
			: [STM.Agent];

		return (
			<Card
				style={{
					backgroundColor: Strings.CSS.White,
					marginLeft: '10px',
					marginRight: '0px',
				}}
			>
				<CardContent>
					<Grid container >
						<Grid item md={9} xs={12} style={{ paddingRight: '5px' }}>
							<TextField
								id={STI.EmailType}
								select
								fullWidth
								label={createRequiredLabel(STI.TType)}
								disabled={!this.state.isEditMode}
								name={STI.EmailType}
								value={this.state.templateValues.emailType}
								onChange={this.handleChange(STI.EmailType)}
							>
								{this.renderMenuItems(emailTypes, false)}
							</TextField>
							<TextField
								id={STI.Title}
								fullWidth
								disabled={!this.state.isEditMode}
								label={createRequiredLabel(STI.TTitle)}
								InputLabelProps={{ shrink: true }}
								value={this.state.templateValues.title}
								onChange={this.handleChange(STI.Title, 60, true)}
								inputProps={{ style: { color: themePalette.primary_text } }}
								InputProps={{ disableUnderline: true }}
							/>
							<TextField
								id={STI.Subject}
								fullWidth
								disabled={!this.state.isEditMode}
								label={createRequiredLabel(STI.ESubject)}
								InputLabelProps={{ shrink: true }}
								value={this.state.templateValues.subject}
								onChange={this.handleChange(STI.Subject, 60, true)}
								inputProps={{ style: { color: themePalette.primary_text } }}
								InputProps={{ disableUnderline: true }}
							/>
							<TextField
								id={STI.Description}
								fullWidth
								label={STI.TDescription}
								InputLabelProps={{ shrink: true }}
								value={this.state.templateValues.description}
								disabled={!this.state.isEditMode}
								onChange={this.handleChange(STI.Description, 360)}
								inputProps={{ style: { color: themePalette.primary_text } }}
								InputProps={{ disableUnderline: true }}
							/>

						</Grid>
						<Grid item md={3} xs={12} style={{ paddingLeft: '5px' }}>
							<FormControlLabel
								control={
									<Switch
										name={STI.IDraft}
										color={Strings.Theming.Primary}
										disabled={!this.state.isEditMode}
										title={STI.Draft}
										checked={this.state.templateValues.isDraft}
										onChange={this.handleSwitchChange}
									/>
								}
								label={STI.Draft}
							/>
							<MultiselectDropdownWithChips
								selected={this.state.templateValues.emailAutomationEventTypeCategory}
								label={STI.TEvent}
								options={this.props.emailAutomationEventTypeCategories}
								setValues={this.handleEventTypeChange}
								disabled={!this.state.isEditMode}
							/>
							<TextField
								label={createRequiredLabel(STI.TLanguage)}
								name={STI.Language}
								select
								fullWidth
								disabled={!this.state.isEditMode}
								value={this.state.templateValues.language}
								onChange={this.handleChange(STI.Language)}
							>
								{this.renderMenuItems(
									this.props.languages,
									false
								)}
							</TextField>
							<MultiselectDropdownWithChips
								selected={this.state.templateValues.linesOfBusiness}
								label={STI.TLOB}
								options={this.props.linesOfBusiness}
								setValues={this.handleLineOfBusinessChange}
								disabled={!this.state.isEditMode}
							/>
						</Grid>
					</Grid>
					<Grid container>
						<Grid item xs={12} style={{ paddingTop: '5px' }}>
							<div style={{ height: 400, marginBottom: '42px' }}>
								<div ref={this.editorHostRef} />
							</div>
						</Grid>
					</Grid>
					<Typography
						align={Strings.CSS.Center}
						variant={Strings.CSS.Caption}
					>
						{STI.Hint}
					</Typography>
					{this.renderVariableDialog()}
					{this.renderLinkDialog()}
				</CardContent>
			</Card>
		);
	};

	renderLinkDialog = (): JSX.Element => {
		return (
			<Dialog
				open={this.state.isLinkDialogOpen}
				onClose={this.closeDialog}
				fullWidth
			>
				<DialogTitle>Insert Link</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						margin="dense"
						label="Display text"
						value={this.state.linkDisplayText}
						onChange={this.handleLinkFieldChange('linkDisplayText')}
					/>
					<TextField
						fullWidth
						margin="dense"
						label="URL"
						placeholder="https://example.com"
						value={this.state.linkUrl}
						onChange={this.handleLinkFieldChange('linkUrl')}
					/>
				</DialogContent>
				<DialogActions>
					<Button color={Strings.Theming.Secondary} onClick={this.closeDialog}>
						{Strings.ButtonText.Cancel}
					</Button>
					<Button
						variant="contained"
						color={Strings.Theming.Primary}
						onClick={this.applyLink}
						disabled={!this.state.linkUrl.trim()}
					>
						Insert
					</Button>
				</DialogActions>
			</Dialog>
		);
	};

	renderMenuItems = (array: string[], isLob: boolean): JSX.Element[] => {
		const menuList: JSX.Element[] = [];

		if (isLob) {
			menuList.push(
				<MenuItem value={STI.All} key={'0LOB'}>
					{STI.All}
				</MenuItem>
			);
		}

		array.forEach((element: string, index: number) => {
			menuList.push(
				<MenuItem value={element} key={index}>
					{element}
				</MenuItem>
			);
		});

		return menuList;
	};

	renderVariableDialog = (): JSX.Element => {
		return (
			<Dialog
				open={this.state.isVariableDialogOpen}
				onClose={this.closeDialog}
			>
				<DialogTitle>{STI.SelectVar}</DialogTitle>
				<MenuList>
					{this.props.substitutions.map((substitution, index) => {
						return (
							<MenuItem
								key={index}
								value={substitution}
								onClick={() => {
									const variable = STI.LeftSub + substitution + STI.RightSub;
									const quill = this.quill;
									let htmlContent = this.state.templateValues.htmlContent || '';

									if (quill) {
										const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
										quill.insertText(range.index, variable, 'user');
										htmlContent = quill.root.innerHTML;
									} else {
										htmlContent += variable;
									}

									const templateValues: Template = {
										...this.state.templateValues,
										htmlContent,
									};

									this.setState({ templateValues, isVariableDialogOpen: false });
								}}
							>
								{substitution}
							</MenuItem>
						);
					})}
				</MenuList>
			</Dialog>
		);
	};

	renderCancelDialog = (): JSX.Element => {
		return (
			<Dialog
				open={this.state.isCancelDialogOpen}
				onClose={this.closeDialog}
			>
				<DialogTitle>{STM.CancelNoSave}</DialogTitle>
				<DialogActions>
					<Button
						color={Strings.Theming.Secondary}
						onClick={this.closeDialog}
					>
						{Strings.ButtonText.Stay}
					</Button>
					<Button
						variant="contained"
						color={Strings.Theming.Primary}
						style={{ backgroundColor: themePalette.delete_remove_reject_button }}
						onClick={() => this.navigateBack()}
					>
						{Strings.ButtonText.Leave}
					</Button>
				</DialogActions>
			</Dialog >
		);
	};

	renderIsInUseDialog = (): JSX.Element => {
		return (
			<Dialog
				open={this.state.isInUseDialogOpen}
				disableEscapeKeyDown
				onClose={this.closeDialog}
			>
				<DialogTitle>
					{(this.props.template
						? this.props.template.title
						: Strings.TemplateManagement.ThisTemplate
					) + Strings.TemplateManagement.InUse}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>{STM.RemoveBeforeEdit}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						variant="contained"
						color={Strings.Theming.Primary}
						style={{ backgroundColor: themePalette.delete_remove_reject_button }}
						onClick={this.closeDialog}
					>
						{Strings.ButtonText.Ok}
					</Button>
				</DialogActions>
			</Dialog>
		);
	};

	renderDeleteDialog = (): JSX.Element => {
		return (
			<Dialog
				open={this.state.isDeleteDialogOpen}
				fullWidth
				onClose={this.closeDialog}
			>
				<DialogTitle>
					{deleteTemplateText(this.props.template.title || '')}
				</DialogTitle>
				<DialogActions>
					<Button
						color={Strings.Theming.Secondary}
						onClick={this.closeDialog}
					>
						{Strings.ButtonText.Cancel}
					</Button>
					<Button
						variant="contained"
						color={Strings.Theming.Primary}
						style={{ backgroundColor: themePalette.delete_remove_reject_button }}
						onClick={this.onDeleteConfirmation}
					>
						{Strings.ButtonText.Delete}
					</Button>
				</DialogActions>
			</Dialog >
		);
	};
}

function mapStateToProps(state: any): StateProps {

	return {
		linesOfBusiness: state.lookup.getLabels(Lookups.LeadLineOfBusiness),
		languages: state.lookup.getLabels(Lookups.Language) || [Strings.Languages.English, Strings.Languages.Spanish],
		template: state.templateManagerState.editedTemplate
			|| state.templateManagerState.currentTemplate,
		isTemplateLoading: state.templateManagerState.areAgentTemplatesLoading
			|| state.templateManagerState.areCorporateTemplatesLoading,
		substitutions: state.lookup.getLabels(Lookups.EmailTemplateSubstitution),
		agentId: state.user.id,
		agentCode: state.user.agentID,
		events: state.emailAutomation.events,
		emailAutomationEventTypeCategories: state.lookup.getLabels(Lookups.EmailAutomationEventTypeCategory),
	};
}

function mapDispatchToProps(dispatch: any): DispatchProps & Partial<NavigationProps> {
	return {
		navigateBack: () =>
			dispatch(navigateBack()),

		navigateTo: (route: string) =>
			dispatch(navigateTo(route)),

		navigateToWithoutAddingToHistory: (route: string) =>
			dispatch(navigateToWithoutAddingToHistory(route)),

		getTemplate: (templateId: string) =>
			dispatch(GetTemplateById.started(templateId)),

		deleteTemplate: (templateId: string) =>
			dispatch(DeleteTemplate.started(templateId)),

		postTemplate: (template: Template) =>
			dispatch(PostTemplate.started(template)),

		getSnackbar: (props: SnackbarProps) =>
			dispatch(QueueSnackbar(props)),

		storeEditedTemplate: (template: Template) =>
			dispatch(StoreEditedTemplate(template)),

		getEmailAutomations: () => dispatch(getEmailAutomationsByAgentId()),
	};
}

export const TemplateContentContainer = connect(mapStateToProps, mapDispatchToProps, true)(TemplateContentPage as React.ComponentType<any>);